
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/bootstrap.min.css';
import '../styles/style.css';
import MainSidebar from '../components/MainSidebar';
import rightarrow from '../assets/images/right.svg';
import passwordeye from '../assets/images/eye.svg';
import config from '../config/config';

// Modal Overlay Component
const ModalOverlay = ({ show, onClick }) => {
  return show ? <div className="modal-backdrop fade show" onClick={onClick}></div> : null;
};

const ChangePassword = () => {
  // State Management
  const [userData, setUserData] = useState({ phone: '', user_name: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showModal, setShowModal] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalEmailError, setModalEmailError] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const navigate = useNavigate();

  // Initial User Data Fetch
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/Login');
      return;
    }

    const fetchUserDetails = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/current-user-details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const userDetails = response.data.data.user_details;
        const user = response.data.data.user;
        setUserData({ ...userDetails, user_name: user.user_name });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user details', error);
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [navigate]);

  // Modal handlers
  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setModalEmail('');
    setModalEmailError('');
    setWordCount(0);
    setModalLoading(false);
  };

  // Form Submission Handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    const token = localStorage.getItem('token');
    const currentPassword = e.target.elements.currentPassword.value;
    const newPassword = e.target.elements.newPassword.value;
    const confirmPassword = e.target.elements.confirmPassword.value;

    // Basic validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage('All password fields are required');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirm password do not match');
      setLoading(false);
      return;
    }
  
    try {
      const response = await axios.post(
        `${config.baseURL}/update-password`,
        {
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.data && response.data.success) {
        setSuccessMessage('Password updated successfully');
        e.target.reset();
      } else {
        setErrorMessage(response.data.message || 'Error updating password');
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error updating password');
      console.error('Error updating password', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreAccess = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalEmailError('');

    if (!modalEmail) {
      setModalEmailError('The Email field is required');
      setModalLoading(false);
      return;
    } else if (!/\S+@\S+\.\S+/.test(modalEmail)) {
      setModalEmailError('Email is not valid.');
      setModalLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${config.baseURL}/forget-password`, {
        email: modalEmail,
      });

      if (response.data.success) {
        setSuccessMessage('If your account exists for this email, a password reset link has been sent.');
        handleClose();
      } else {
        setModalEmailError(response.data.message || 'Failed to send reset link.');
      }
    } catch (error) {
      setModalEmailError('An error occurred. Please try again later.');
      console.error('Forgot password error:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Navigation Handler
  const handleBackClick = () => {
    navigate(-1);
  };

  // Password Visibility Toggles
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Email Character Counter
  useEffect(() => {
    setWordCount(modalEmail.length);
  }, [modalEmail]);

  return (
    <div className="main_wrapper">
      <div className="main_patreonpass_wrapper">
        <div className="patreonpass_container">
          {/* Sidebar */}
          <div className="left_sidebar">
            <MainSidebar />
          </div>

          {/* Main Content */}
          <div className="patreonpass_main_content edit_profile_settings" id="content">
            {/* Left Column */}
            <div className="left_main_content">
              <div className="feed_wrapper">
                <div className="page_title_with_dropdown notification_page">
                  <div className="page_heading">
                    <button className="profile_back_btn" onClick={handleBackClick}>
                      <svg width={54} height={54} viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M33.75 40.5L20.25 27L33.75 13.5" stroke="black" strokeWidth="2"/>
                      </svg>
                    </button>
                    <h1>SETTINGS</h1>
                  </div>
                </div>
                <div className="menus_all_wrapper">
                  <div className="edit_settings_menu_list">
                    <h3><a href={`/${userData.user_name}`}>@{userData.user_name}</a></h3>
                    <ul>
                      <li><Link to="/EditProfile">Profile <span className="menu_arrow"><img src={rightarrow} alt="" /></span></Link></li>
                      <li><Link className="active_profile_menu" to="/AccountSettings">Account <span className="menu_arrow"><img src={rightarrow} alt="" /></span></Link></li>
                      <li><Link to="/PrivacySafetySettings">Privacy and safety <span className="menu_arrow"><img src={rightarrow} alt="" /></span></Link></li>
                      <li><Link to="/NotificationSettings">Notifications <span className="menu_arrow"><img src={rightarrow} alt="" /></span></Link></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="right_main_content">
              <form onSubmit={handleSubmit}>
                <div className="page_title_with_dropdown notification_page">
                  <div className="page_heading">                    
                    <h1>CHANGE PASSWORD</h1>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="fields_menu_bottom_box_edit_account edit_username_fields">
                  {/* Current Password */}
                  <div className="editable_fields_user edit_username_field edit_phonenumber">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="pwd_side mb-4 position_relative">
                          <input
                            type={showPassword.current ? 'text' : 'password'}
                            className="form-control"
                            id="currentPassword"
                            name="currentPassword"                            
                            placeholder=""
                          />
                          <label htmlFor="currentPassword" className="placeholder-label">
                            Current password
                          </label>
                          <svg
                            onClick={() => togglePasswordVisibility('current')}
                            className="password-toggle"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M13.0948 4.01374C6.38528 3.698 2.5081 8.89752 1.19816 11.9602C1.06239 12.2776 1.08062 12.6387 1.23356 12.9483C4.76514 20.0956 10.5792 21.1041 13.0948 20.6962C18.3737 20.0659 21.6494 15.4788 22.8164 12.8926C22.9419 12.6144 22.9497 12.2985 22.8443 12.0121C20.7294 6.26781 15.501 4.28486 13.0948 4.01374Z" stroke="#8A96A3" stroke-width="2"></path>
                            <path d="M10.9932 8.28101C10.8607 7.72995 11.2019 7.16467 11.7683 7.14475C12.4657 7.12024 13.1644 7.23872 13.8204 7.49714C14.7966 7.88169 15.6307 8.55747 16.2094 9.43263C16.7881 10.3078 17.0834 11.3399 17.055 12.3887C17.0266 13.4376 16.6761 14.4522 16.0509 15.2948C15.4257 16.1374 14.5563 16.7671 13.5607 17.0983C12.5652 17.4296 11.4918 17.4463 10.4865 17.1461C9.4811 16.846 8.59252 16.2436 7.94146 15.4209C7.50397 14.868 7.18798 14.2336 7.00926 13.5591C6.8641 13.0112 7.30617 12.5207 7.87198 12.4878C8.43778 12.4549 8.90784 12.9003 9.12534 13.4236C9.23263 13.6818 9.37545 13.9255 9.55095 14.1473C9.94046 14.6395 10.4721 14.9999 11.0735 15.1795C11.675 15.359 12.3172 15.349 12.9128 15.1509C13.5084 14.9527 14.0286 14.576 14.4026 14.0719C14.7766 13.5678 14.9864 12.9607 15.0033 12.3332C15.0203 11.7058 14.8437 11.0883 14.4974 10.5647C14.1512 10.0411 13.6522 9.63681 13.0682 9.40674C12.805 9.30308 12.5304 9.23705 12.2522 9.20922C11.6882 9.15279 11.1257 8.83206 10.9932 8.28101Z" fill="#8A96A3"></path>
                            <path d="M7.99746 11.22C7.44905 11.077 7.11156 10.5094 7.36118 10.0006C7.64979 9.41226 8.04905 8.88475 8.53688 8.44723C8.95881 8.06881 9.5967 8.23949 9.88332 8.72844C10.1699 9.21739 9.987 9.83856 9.61979 10.2703C9.54458 10.3587 9.47447 10.4513 9.40979 10.5477C9.09402 11.0184 8.54587 11.3631 7.99746 11.22Z" fill="#8A96A3"></path>
                          </svg> 
                          <div className="forgot-password-link">
                            <a href="javascript:void(0)" onClick={handleShow}>Forgot Password?</a>
                          </div> 
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* New Password Fields */}
                  <div className="editable_fields_user edit_username_field edit_phonenumber line_with_before_element">
                    <div className="row">
                      <div className="col-md-12">
                        {/* New Password */}
                        <div className="mb-4 pwd_side position_relative">
                          <input
                            type={showPassword.new ? 'text' : 'password'}
                            className="form-control"
                            id="newPassword"
                            name="newPassword"                            
                            placeholder=""
                          />
                          <label htmlFor="newPassword" className="placeholder-label">
                            New password
                          </label>
                          <svg
                            onClick={() => togglePasswordVisibility('new')}
                            className="password-toggle"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M13.0948 4.01374C6.38528 3.698 2.5081 8.89752 1.19816 11.9602C1.06239 12.2776 1.08062 12.6387 1.23356 12.9483C4.76514 20.0956 10.5792 21.1041 13.0948 20.6962C18.3737 20.0659 21.6494 15.4788 22.8164 12.8926C22.9419 12.6144 22.9497 12.2985 22.8443 12.0121C20.7294 6.26781 15.501 4.28486 13.0948 4.01374Z" stroke="#8A96A3" stroke-width="2"></path>
                            <path d="M10.9932 8.28101C10.8607 7.72995 11.2019 7.16467 11.7683 7.14475C12.4657 7.12024 13.1644 7.23872 13.8204 7.49714C14.7966 7.88169 15.6307 8.55747 16.2094 9.43263C16.7881 10.3078 17.0834 11.3399 17.055 12.3887C17.0266 13.4376 16.6761 14.4522 16.0509 15.2948C15.4257 16.1374 14.5563 16.7671 13.5607 17.0983C12.5652 17.4296 11.4918 17.4463 10.4865 17.1461C9.4811 16.846 8.59252 16.2436 7.94146 15.4209C7.50397 14.868 7.18798 14.2336 7.00926 13.5591C6.8641 13.0112 7.30617 12.5207 7.87198 12.4878C8.43778 12.4549 8.90784 12.9003 9.12534 13.4236C9.23263 13.6818 9.37545 13.9255 9.55095 14.1473C9.94046 14.6395 10.4721 14.9999 11.0735 15.1795C11.675 15.359 12.3172 15.349 12.9128 15.1509C13.5084 14.9527 14.0286 14.576 14.4026 14.0719C14.7766 13.5678 14.9864 12.9607 15.0033 12.3332C15.0203 11.7058 14.8437 11.0883 14.4974 10.5647C14.1512 10.0411 13.6522 9.63681 13.0682 9.40674C12.805 9.30308 12.5304 9.23705 12.2522 9.20922C11.6882 9.15279 11.1257 8.83206 10.9932 8.28101Z" fill="#8A96A3"></path>
                            <path d="M7.99746 11.22C7.44905 11.077 7.11156 10.5094 7.36118 10.0006C7.64979 9.41226 8.04905 8.88475 8.53688 8.44723C8.95881 8.06881 9.5967 8.23949 9.88332 8.72844C10.1699 9.21739 9.987 9.83856 9.61979 10.2703C9.54458 10.3587 9.47447 10.4513 9.40979 10.5477C9.09402 11.0184 8.54587 11.3631 7.99746 11.22Z" fill="#8A96A3"></path>
                          </svg>
                        </div>

                        {/* Confirm Password */}
                        <div className="mb-4 pwd_side position_relative">
                          <input
                            type={showPassword.confirm ? 'text' : 'password'}
                            className="form-control"
                            id="confirmPassword"
                            name="confirmPassword"                            
                            placeholder=""
                          />
                          <label htmlFor="confirmPassword" className="placeholder-label">
                            Confirm new password
                          </label>
                          <svg
                            onClick={() => togglePasswordVisibility('confirm')}
                            className="password-toggle"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M13.0948 4.01374C6.38528 3.698 2.5081 8.89752 1.19816 11.9602C1.06239 12.2776 1.08062 12.6387 1.23356 12.9483C4.76514 20.0956 10.5792 21.1041 13.0948 20.6962C18.3737 20.0659 21.6494 15.4788 22.8164 12.8926C22.9419 12.6144 22.9497 12.2985 22.8443 12.0121C20.7294 6.26781 15.501 4.28486 13.0948 4.01374Z" stroke="#8A96A3" stroke-width="2"></path>
                            <path d="M10.9932 8.28101C10.8607 7.72995 11.2019 7.16467 11.7683 7.14475C12.4657 7.12024 13.1644 7.23872 13.8204 7.49714C14.7966 7.88169 15.6307 8.55747 16.2094 9.43263C16.7881 10.3078 17.0834 11.3399 17.055 12.3887C17.0266 13.4376 16.6761 14.4522 16.0509 15.2948C15.4257 16.1374 14.5563 16.7671 13.5607 17.0983C12.5652 17.4296 11.4918 17.4463 10.4865 17.1461C9.4811 16.846 8.59252 16.2436 7.94146 15.4209C7.50397 14.868 7.18798 14.2336 7.00926 13.5591C6.8641 13.0112 7.30617 12.5207 7.87198 12.4878C8.43778 12.4549 8.90784 12.9003 9.12534 13.4236C9.23263 13.6818 9.37545 13.9255 9.55095 14.1473C9.94046 14.6395 10.4721 14.9999 11.0735 15.1795C11.675 15.359 12.3172 15.349 12.9128 15.1509C13.5084 14.9527 14.0286 14.576 14.4026 14.0719C14.7766 13.5678 14.9864 12.9607 15.0033 12.3332C15.0203 11.7058 14.8437 11.0883 14.4974 10.5647C14.1512 10.0411 13.6522 9.63681 13.0682 9.40674C12.805 9.30308 12.5304 9.23705 12.2522 9.20922C11.6882 9.15279 11.1257 8.83206 10.9932 8.28101Z" fill="#8A96A3"></path>
                            <path d="M7.99746 11.22C7.44905 11.077 7.11156 10.5094 7.36118 10.0006C7.64979 9.41226 8.04905 8.88475 8.53688 8.44723C8.95881 8.06881 9.5967 8.23949 9.88332 8.72844C10.1699 9.21739 9.987 9.83856 9.61979 10.2703C9.54458 10.3587 9.47447 10.4513 9.40979 10.5477C9.09402 11.0184 8.54587 11.3631 7.99746 11.22Z" fill="#8A96A3"></path>
                          </svg>
                        </div>

                        {/* Error/Success Messages */}
                        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                        {successMessage && <div className="alert alert-success">{successMessage}</div>}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="divider_line" />
                    <div className="row">
                      <div className="col-md-12 text-right">
                        <button 
                          type="submit" 
                          disabled={loading}
                          aria-disabled={loading}
                          className="btn btn-primary"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Saving...
                            </>
                          ) : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Restore Access Modal */}
      {showModal && (
        <>
          <ModalOverlay show={showModal} onClick={handleClose} />
          <div className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="restoreAccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="restoreAccessModalLabel">Restore Access</h5>
                </div>
                <div className="modal-body">
                  <form id="restoreAccessForm" onSubmit={handleRestoreAccess}>
                    <p>If you have a PatreonPass account, you will receive a password reset link to this e-mail.</p>
                    <div className="position_relative">
                      <input 
                        type="email" 
                        className={`form-control ${modalEmailError ? 'is-invalid' : ''}`} 
                        id="restoreAccessEmail" 
                        placeholder="Email" 
                        value={modalEmail}
                        onChange={(e) => setModalEmail(e.target.value)}
                      />
                      <label htmlFor="restoreAccessEmail" className="placeholder-label">Email</label>
                      {modalEmailError && <div className="invalid-feedback">{modalEmailError}</div>}
                    </div>                   
                    <div className="word_count">
                      <span>{wordCount} / 64</span>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary send_btn" onClick={handleClose}>Cancel</button>
                      <button 
                        type="submit" 
                        disabled={modalLoading}
                        aria-disabled={modalLoading}
                        className="btn btn-primary send_btn remove_right_side_margin"
                      >
                        {modalLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : 'Send'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangePassword;
